import { toast } from "react-toastify";
import { check_token_expired_logout } from "../user/userAction";
import {
  GET_ALL_FAQS,
  GET_PRIVACY_POLICY_DATA,
  GET_TERM_DATA,
  GET_COKIE_POLICY_DATA,
  GET_HOW_IT_WORK_DATA,
  GET_ABOUT_DATA,
  GET_FAQ_DATA,
  GET_DPA_DATA,
  GET_RETURN_POLICY_DATA,
  GET_PRIVACY_POLICY_DATA_NL,
  GET_TERM_DATA_NL,
  GET_COKIE_POLICY_DATA_NL,
  GET_HOW_IT_WORK_DATA_NL,
  GET_ABOUT_DATA_NL,
  GET_DPA_DATA_NL,
  GET_RETURN_POLICY_DATA_NL,
  GET_FAQ_DATA_NL,
  GET_ALL_FAQS_NL,
  GET_NEWSLETTER_CONTENTNL,
  GET_HOME_CONTENTNL,
  GET_NEWSLETTER_CONTENT,
  GET_HOMEPAGE_HERO_SLIDES,
  GET_HOMEPAGE_HERO_SLIDES_NL,
  GET_CMS_DOCUMENTS,
  GET_CMS_DOCUMENT,
  GET_CMS_DOCUMENT_VERSIONS,
  GET_ARTICLES,
} from "./cmsTypes";
import apiClient from "../../api/apiClient";

const unwrap_document_response = (response) => {
  const body = response?.data ?? response ?? {};
  return body?.data ?? body;
};

const normalize_cms_document = (document = {}) => ({
  ...document,
  id: document.id ?? document._id ?? document.documentId ?? document.document_id,
  documentId: document.documentId ?? document.document_id ?? document.id ?? document._id,
  title: document.title ?? document.name ?? "Untitled document",
  titleNl: document.title_nl ?? document.titleNl ?? document.name_nl ?? "",
  type: document.type ?? document.documentType ?? document.document_type ?? "",
  language: document.language ?? document.locale ?? "",
  visibility: document.visibility ?? document.audience ?? "EVERYONE",
  content: document.content ?? document.body ?? "",
  version: document.version ?? document.versionNumber ?? document.version_number ?? 1,
  status: String(document.status ?? document.document_status ?? "DRAFT").toUpperCase(),
  createdAt: document.createdAt ?? document.created_at,
  updatedAt: document.updatedAt ?? document.updated_at,
  publishedAt: document.publishedAt ?? document.published_at,
  createdBy: document.createdBy ?? document.created_by,
  updatedBy: document.updatedBy ?? document.updated_by,
  publishedBy: document.publishedBy ?? document.published_by,
  changeSummary: document.changeSummary ?? document.change_summary ?? "",
  isCurrent: document.isCurrent ?? document.is_current ?? document.current ?? false,
});

export const get_cms_documents = async (params = {}, dispatch) => {
  try {
    const data = unwrap_document_response(await apiClient.get("/cms/documents", { params }));
    const documents = data.documents ?? data.items ?? data.results ?? (Array.isArray(data) ? data : []);
    const pagination = data.pagination ?? data.meta ?? {};
    const result = {
      documents: documents.map(normalize_cms_document),
      total: Number(data.total ?? data.totalItems ?? pagination.total ?? pagination.totalItems ?? documents.length),
      page: Number(data.page ?? pagination.page ?? params.page ?? 1),
      limit: Number(data.limit ?? pagination.limit ?? params.limit ?? 10),
      totalPages: Number(data.totalPages ?? pagination.totalPages ?? 0),
    };
    dispatch({ type: GET_CMS_DOCUMENTS, payload: result });
    return result;
  } catch (error) {
    check_token_expired_logout(error, dispatch);
    throw error;
  }
};

export const get_cms_document_by_id = async (id, dispatch) => {
  try {
    const response = await apiClient.get(`/cms/documents/${id}`);
    const document = normalize_cms_document(unwrap_document_response(response));
    dispatch({ type: GET_CMS_DOCUMENT, payload: document });
    return document;
  } catch (error) {
    check_token_expired_logout(error, dispatch);
    throw error;
  }
};

export const get_cms_document_versions = async (id, dispatch) => {
  try {
    const data = unwrap_document_response(await apiClient.get(`/cms/documents/${id}/versions`));
    const versions = data.versions ?? data.items ?? (Array.isArray(data) ? data : []);
    const document_versions = versions.map(normalize_cms_document);
    dispatch({ type: GET_CMS_DOCUMENT_VERSIONS, payload: document_versions });
    return document_versions;
  } catch (error) {
    check_token_expired_logout(error, dispatch);
    throw error;
  }
};

export const create_cms_documents = async (payload, dispatch) => {
  try {
    return await apiClient.post("/cms/documents", payload);
  } catch (error) {
    check_token_expired_logout(error, dispatch);
    throw error;
  }
};

export const update_cms_documents = async (id, payload, dispatch) => {
  try {
    return await apiClient.put(`/cms/documents/${id}`, payload);
  } catch (error) {
    check_token_expired_logout(error, dispatch);
    throw error;
  }
};

export const publish_cms_documents = async (id, dispatch) => {
  try {
    return await apiClient.post(`/cms/documents/${id}/publish`);
  } catch (error) {
    check_token_expired_logout(error, dispatch);
    throw error;
  }
};

export const get_homepage_hero_slides = async (contentType, dispatch) => {
  try {
    const res = await apiClient.get(`cms/content/${contentType}`);
    if (!res.data?.status) throw new Error(res.data?.message || "Unable to load hero slides");
    dispatch({
      type: contentType.endsWith("_nl") ? GET_HOMEPAGE_HERO_SLIDES_NL : GET_HOMEPAGE_HERO_SLIDES,
      payload: res.data.data,
    });
    return res.data.data;
  } catch (error) {
    check_token_expired_logout(error, dispatch);
    throw error;
  }
};

export const save_homepage_hero_slides = async (payload, id, dispatch) => {
  const urlPath = id ? `cms/edit-content/${id}` : "cms/add-content";
  try {
    const res = await apiClient.post(urlPath, payload);
    if (!res.data?.status) throw new Error(res.data?.message || "Unable to save hero slides");
    toast.success(res.data.message || "Homepage hero slides saved", { toastId: "saveHomeSlide", autoClose: 2000 });
    return res.data;
  } catch (error) {
    check_token_expired_logout(error, dispatch);
    throw error;
  }
};

export const upload_homepage_hero_image = async (file, dispatch) => {
  if (!(file instanceof Blob) || file.size === 0) {
    throw new Error("A valid image file is required.");
  }

  const formData = new FormData();
  const extensionByType = {
    "image/jpeg": "jpg",
    "image/jpg": "jpg",
    "image/png": "png",
    "image/webp": "webp",
  };
  const extension = extensionByType[file.type] || "jpg";
  const fileName = file.name || `homepage-hero-${Date.now()}.${extension}`;
  formData.append("images", file, fileName);

  try {
    // Do not set Content-Type manually. The browser must add the multipart
    // boundary or the upload middleware will receive an empty files array.
    const response = await apiClient.post("cms/upload-image", formData);
    if (!response.data?.status) {
      throw new Error(response.data?.error || "Unable to upload hero image.");
    }
    return response;
  } catch (error) {
    check_token_expired_logout(error, dispatch);
    throw error;
  }
};


export const create_faq_question = async (form_data, id, dispatch) => {
  try {
    const urlPath = id ? `cms/edit-content/${id}` : `cms/add-content`;
    const res = await apiClient.post(urlPath, form_data);
    if (res.data && res.data.status) {
      toast.success(res.data.message, { toastId: "addeditem1", autoClose: 2000 });
      return true;
    }
  } catch (error) {
    check_token_expired_logout(error, dispatch);
  }
};

// get common cms content get api function 
export const get_all_faq_questions = async (question_type, dispatch) => {
  try {
    const urlPath = `cms/content/${question_type}`;
    const res = await apiClient.get(urlPath);
    if (res.data?.status) {
      const data = res.data.data;
      const specialTypes = {
        'newsletter_content': GET_NEWSLETTER_CONTENT,
        'newsletter_content_nl': GET_NEWSLETTER_CONTENTNL,
        'termsandconditions': GET_NEWSLETTER_CONTENT,
        'faqContent': GET_ALL_FAQS,
        'faqContent_nl': GET_ALL_FAQS_NL,
        'home_slides_content_nl': GET_HOME_CONTENTNL
      };
      if (specialTypes[question_type]) {
        dispatch({ type: specialTypes[question_type], payload: data });
        return;
      }
      if (question_type.endsWith('-nl')) {
        dispatch({ type: GET_ALL_FAQS_NL, payload: data });
      } else {
        dispatch({ type: GET_ALL_FAQS, payload: data });
      }
    }
  } catch (error) {
    check_token_expired_logout(error);
  }
};

// handle sequence change 
export const update_items_sequence = async (sequencearr, question_type) => {
  try {
    const urlPath = `admin/update-sequence/${question_type}`;
    const res = await apiClient.put(urlPath, sequencearr);
    if (res.data && res.data.status) {
      toast.success(res.data.message);
      return true;
    } else {
      return false;
    }
  } catch (error) {
    toast.error(error.response.data.error);
    check_token_expired_logout(error);
  }
};

// upload cms images 
export const upload_cms_photo = async (photo, dispatch) => {
  const formData = new FormData();
  photo.forEach((v) => { formData.append(`images`, v); });
  try {
    const urlPath = `cms/upload-image`;
    const res = await apiClient.post(urlPath, formData, {
      headers: { "Content-Type": "multipart/form-data", },
    });
    if (res.data && res.data.status) {
      return res;
    } else {
      toast.error(res.error, { toastId: "editphotoerrr", autoClose: 2000 });
      return false;
    }
  } catch (error) {
    check_token_expired_logout(error, dispatch);
  }
};

// get privacy policy data 
export const get_privacy_policy_data = async (content_type, dispatch) => {
  try {
    const urlPath = `cms/content/${content_type}`;
    const res = await apiClient.get(urlPath);
    if (res.data && res.data.status) {
      if (content_type === 'privacy_policy') {
        dispatch({ type: GET_PRIVACY_POLICY_DATA, payload: res.data.data });
      }
      else {
        dispatch({ type: GET_PRIVACY_POLICY_DATA_NL, payload: res.data.data });
      }
    }
  } catch (error) {
    check_token_expired_logout(error);
  }
};

// get faq data 
export const get_faq_data = async (content_type, dispatch) => {
  try {
    const urlPath = `cms/content/${content_type}`;
    const res = await apiClient.get(urlPath);
    if (res.data && res.data.status) {
      if (content_type === 'faqContent') {
        dispatch({ type: GET_FAQ_DATA, payload: res.data.data });
      } else {
        dispatch({ type: GET_FAQ_DATA_NL, payload: res.data.data });
      }
    }
  } catch (error) {
    check_token_expired_logout(error);
  }
};

// get term and condition data 
export const get_term_data = async (content_type, dispatch) => {
  try {
    const urlPath = `cms/content/${content_type}`;
    const res = await apiClient.get(urlPath);
    if (res.data && res.data.status) {
      if (content_type === 'termsandconditions') {
        dispatch({ type: GET_TERM_DATA, payload: res.data.data });
      }
      else {
        dispatch({ type: GET_TERM_DATA_NL, payload: res.data.data });
      }
    }
  } catch (error) {
    check_token_expired_logout(error);
  }
};

// get cookie policy data 
export const get_cookie_policy_data = async (content_type, dispatch) => {
  try {
    const urlPath = `cms/content/${content_type}`;
    const res = await apiClient.get(urlPath);
    if (res.data && res.data.status) {
      if (content_type === 'cookie_policy') {
        dispatch({ type: GET_COKIE_POLICY_DATA, payload: res.data.data });
      } else {
        dispatch({ type: GET_COKIE_POLICY_DATA_NL, payload: res.data.data });
      }
    }
  } catch (error) {
    check_token_expired_logout(error);
  }
};

// get how it work data 
export const get_how_it_work_data = async (content_type, dispatch) => {
  try {
    const urlPath = `cms/content/${content_type}`;
    const res = await apiClient.get(urlPath);
    if (res.data && res.data.status) {
      if (content_type === 'how_it_work') {
        dispatch({ type: GET_HOW_IT_WORK_DATA, payload: res.data.data });
      } else {
        dispatch({ type: GET_HOW_IT_WORK_DATA_NL, payload: res.data.data });
      }
    }
  } catch (error) {
    check_token_expired_logout(error);
  }
};

// get about us data 
export const get_about_data = async (content_type, dispatch) => {
  try {
    const urlPath = `cms/content/${content_type}`;

    const res = await apiClient.get(urlPath);

    if (res.data && res.data.status) {
      if (content_type === 'aboutus') {
        dispatch({ type: GET_ABOUT_DATA, payload: res.data.data });
      } else {
        dispatch({ type: GET_ABOUT_DATA_NL, payload: res.data.data });
      }
    }
  } catch (error) {
    check_token_expired_logout(error);
  }
};

// get data processing agrement data 
export const get_dpa_data = async (content_type, dispatch) => {
  try {
    const urlPath = `cms/content/${content_type}`;
    const res = await apiClient.get(urlPath);
    if (res.data && res.data.status) {
      if (content_type === 'data_processing_agreement') {
        dispatch({ type: GET_DPA_DATA, payload: res.data.data });
      } else {
        dispatch({ type: GET_DPA_DATA_NL, payload: res.data.data });
      }
    }
  } catch (error) {
    check_token_expired_logout(error);
  }
};

// get return policy data 
export const get_return_policy_data = async (content_type, dispatch) => {
  try {
    const urlPath = `cms/content/${content_type}`;
    const res = await apiClient.get(urlPath);
    if (res.data && res.data.status) {
      if (content_type === 'return_policy') {
        dispatch({ type: GET_RETURN_POLICY_DATA, payload: res.data.data });
      } else {
        dispatch({ type: GET_RETURN_POLICY_DATA_NL, payload: res.data.data });
      }
    }
  } catch (error) {
    check_token_expired_logout(error);
  }
};


// add article function
export const create_article = async (dispatch, data) => {
  try {
    const urlPath = `cms/articles`;
    const res = await apiClient.post(urlPath, data, {
      headers: { "Content-Type": "application/json" },
    });
    if (res.data && res.data.status) {
      toast.success(res.data.message, { toastId: "create_article", autoClose: 1000 });
      return true;
    } else {
      toast.error(res.data.message, { toastId: "create_article_err", autoClose: 1000, });
      return false;
    }
  } catch (error) {
    check_token_expired_logout(error, dispatch);
    return false;
  }
};


// get articles data
export const get_articles = async (dispatch, page = 1, limit = 20, search = "", category = "", status = "") => {
  try {
    const res = await apiClient.get(`cms/articles`, {
      params: { page, limit, search: search || undefined, category: category || undefined, status: status || undefined },
    });
    if (res.data && res.data.status) {
      dispatch({ type: GET_ARTICLES, payload: res.data });
    }
  } catch (error) {
    check_token_expired_logout(error, dispatch);
  }
};

// edit article function
export const update_article = async (dispatch, id, data) => {
  try {
    const res = await apiClient.put(`cms/articles/${id}`, data, {
      headers: { "Content-Type": "application/json" },
    });
    if (res.data && res.data.status) {
      toast.success(res.data.message, { toastId: "update_article", autoClose: 1000 });
      return true;
    }
    toast.error(res.data.message, { toastId: "update_article_err", autoClose: 1000 });
    return false;
  } catch (error) {
    check_token_expired_logout(error, dispatch);
    return false;
  }
};

// delete article function
export const delete_article = async (dispatch, id) => {
  try {
    const res = await apiClient.delete(`cms/articles/${id}`);
    if (res.data && res.data.status) {
      toast.success(res.data.message, { toastId: "delete_article", autoClose: 1000 });
      return true;
    }
    toast.error(res.data.message, { toastId: "delete_article_err", autoClose: 1000 });
    return false;
  } catch (error) {
    check_token_expired_logout(error, dispatch);
    return false;
  }
};
